// File: handleIncomingSignal.js
export async function handleIncomingSignal(data, socket, peerConnection) {
  const { type, sender, offer, answer, candidate } = data;

  try {
    switch (type) {
      case "offer":
        if (!offer || !offer.sdp || !offer.type) {
          console.error("🚫 Invalid offer received:", offer);
          return;
        }
        if (sender === socket.id) return; // avoid handling our own offer

        await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

        // 🔁 Flush queued ICE candidates with deduplication
        const flushed = new Set();
        if (peerConnection.pendingIceCandidates?.length) {
          for (const c of peerConnection.pendingIceCandidates) {
            if (!flushed.has(c.candidate)) {
              await peerConnection.addIceCandidate(new RTCIceCandidate(c));
              flushed.add(c.candidate);
            }
          }
          peerConnection.pendingIceCandidates = [];
        }

        const answerObj = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answerObj);

        socket.emit("signal", {
          type: "answer",
          target: sender,
          answer: answerObj,
          sender: socket.id,
        });
        break;

      case "answer":
        if (!answer || !answer.sdp || !answer.type) {
          console.error("🚫 Invalid answer received:", answer);
          return;
        }

        await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));

        // 🔁 Flush queued ICE candidates with deduplication
        const flushedAnswer = new Set();
        if (peerConnection.pendingIceCandidates?.length) {
          console.log(`📡 Flushing ${peerConnection.pendingIceCandidates.length} queued ICE candidates`);
          for (const c of peerConnection.pendingIceCandidates) {
            if (!flushedAnswer.has(c.candidate)) {
              await peerConnection.addIceCandidate(new RTCIceCandidate(c));
              flushedAnswer.add(c.candidate);
            }
          }
          peerConnection.pendingIceCandidates = [];
        }
        break;

      case "iceCandidate":
        if (!candidate) return;

        // Initialize candidate tracking if not already
        peerConnection._addedIceCandidates = peerConnection._addedIceCandidates || new Set();
        peerConnection.pendingIceCandidates = peerConnection.pendingIceCandidates || [];

        const key = candidate.candidate;

        if (
          !peerConnection.remoteDescription ||
          peerConnection.remoteDescription.type === "rollback"
        ) {
          const alreadyQueued = peerConnection.pendingIceCandidates.some(c => c.candidate === key);
          if (!alreadyQueued) {
            peerConnection.pendingIceCandidates.push(candidate);
          }
        } else {
          if (!peerConnection._addedIceCandidates.has(key)) {
            await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
            peerConnection._addedIceCandidates.add(key);
          } else {
            console.log("🧊 Skipped duplicate ICE candidate (already added)");
          }
        }
        break;

      default:
        console.warn("⚠️ Unknown signal type:", type);
    }
  } catch (err) {
    console.error("❌ Error handling signal:", err);
  }
}
